import {
  faCalendar,
  faChartBar,
  faChevronDown,
  faChevronRight,
  faCircleHalfStroke,
  faCog,
  faEnvelope,
  faFile,
  faFileLines,
  faFileShield,
  faHeart,
  faLocationDot,
  faPeopleArrows,
  faShoppingCart,
  faUser,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LogoutOutlined } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router"; // Import useLocation
import { useAuthStore } from "../Zustand/AuthStore";
import { useTokenStore } from "../Zustand/TokenStore";
import { LoginResponse, Module } from "../types";

type Props = {
  sidebarOpen: boolean;
  onClose: () => void;
};

function Sidebar({ sidebarOpen, onClose }: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const authdata: LoginResponse = useTokenStore((state) => state.AuthData);
  const modules: Module[] = authdata.Modules;
  const [expandedModules, setExpandedModules] = useState<Set<number>>(
    new Set()
  );
  const { signout } = useAuthStore();
  const { clearAuthData, clearToken } = useTokenStore();

  useEffect(() => {
    if (window.innerWidth < 768) {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      // If sidebar is closed, collapse all modules when the open state changes
      setExpandedModules(new Set());
    }
  }, [open]);

  const handleLogout = async () => {
    clearAuthData();
    clearToken();
    signout();
    navigate("/");
    // try {
    //   const response = await AxiosInstance.put(
    //     `${BASE_API_URL}/admin/auth/v1/update-logout-logs/${user_id}`
    //   );

    //   if (response.status === 200) {
    //     clearAuthData();
    //     clearToken();
    //     signout();
    //     navigate("/");
    //   }
    // } catch (error) {
    //   console.error("Error logging out:", error);
    // }
  };

  const handleModuleClick = (moduleId: number) => {
    setExpandedModules((prev) => {
      setOpen(true);
      const newExpandedModules = new Set(prev);
      if (newExpandedModules.has(moduleId)) {
        newExpandedModules.delete(moduleId);
      } else {
        newExpandedModules.add(moduleId);
      }
      return newExpandedModules;
    });
  };

  const variants = {
    open: {
      opacity: 1,
      height: "auto",
      transition: { duration: 0.3, ease: "easeInOut" },
    },
    collapsed: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.3, ease: "easeInOut" },
    },
  };

  const onMouseEnter = () => {
    setOpen(true);
  };

  const onMouseLeave = () => {
    setOpen(false);
  };

  return (
    <div
      onMouseOver={onMouseEnter}
      onMouseOut={onMouseLeave}
      className={`${open ? "md:w-64" : "md:w-[5dvw]"} ${
        sidebarOpen ? "md:w-[20dvw] w-[100dvw]" : "hidden"
      }  relative flex flex-col items-center h-full text-white bg-primary duration-300 md:mt-2`}
    >
      {/* <img
        src={control}
        alt="control icon"
        className={`absolute cursor-pointer -right-3 top-9 w-5 
           border-2 rounded-full hidden md:inline-block mt-5 ${
             !open && "rotate-180"
           }`}
        onClick={() => {
          setOpen(!open);
        }}
      ></img> */}

      <div className="flex flex-col items-center mt-10 overflow-y-scroll h-[80vh] no-scrollbar pt-4 w-full p-2 md:p-1">
        {modules.map((module) => (
          <div
            key={module.Module_Id}
            // className="flex items-center justify-center py-2 gap-x-4"
            className="w-full p-1 m-1"
          >
            <button
              onClick={() => {
                handleModuleClick(module.Module_Id);
              }}
              className="flex items-center w-full py-1 font-medium text-white transition duration-300 bg-transparent border-none rounded group focus:outline-none hover:bg-primary_dark hover:text-white lg:px-3 md:px-0"
            >
              {/* <FontAwesomeIcon
                icon={getModuleIcon(module.Module_Icon)}
                className="text-xl text-blue-300"
              /> */}
              <img
                src={"/" + module.Module_Icon}
                alt={module.Module_Name}
                className="object-contain object-center w-6 h-6 md:w-7 md:h-7"
              ></img>
              <span
                className={`${
                  !open && "md:hidden"
                } origin-left duration-200 ml-4 inline-block}`}
              >
                {module.Module_Name}
              </span>

              {module.Pages.length > 0 && (
                <FontAwesomeIcon
                  icon={
                    expandedModules.has(module.Module_Id)
                      ? faChevronDown
                      : faChevronRight
                  }
                  className={`${
                    !open && "md:hidden"
                  } ml-auto mr-2 size-3 gap-2 justify-self-end inline-block`}
                />
              )}
            </button>

            <AnimatePresence>
              {expandedModules.has(module.Module_Id) && (
                <motion.div
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={variants}
                >
                  <ul>
                    {module.Pages.map((page) => (
                      <li
                        key={page.Page_Id}
                        onClick={() => {
                          navigate(page.Page_Url);
                          onClose();
                        }}
                        className="w-full p-1 pl-8 text-base font-normal text-white transition duration-300 rounded-md cursor-pointer hover:bg-primary_dark hover:text-white "
                      >
                        <FontAwesomeIcon
                          icon={getModuleIcon(page.Page_Icon)}
                          className="text-sm text-blue-300"
                        />
                        <span
                          className={`${
                            !open && "md:hidden"
                          } transition-all duration-200 ease-in-out group-hover:text-lg origin-left ml-4 inline-block}`}
                        >
                          {page.Page_Name}
                        </span>
                      </li>
                    ))}
                    {module.Submodules.map((submodule) => (
                      <div key={submodule.Id} className="w-full">
                        <button
                          onClick={() => handleModuleClick(submodule.Id)}
                          className="flex items-center w-full py-2 font-medium text-white transition duration-300 bg-transparent border-none rounded group focus:outline-none hover:bg-primary_dark hover:text-white"
                        >
                          <FontAwesomeIcon
                            icon={
                              expandedModules.has(submodule.Id)
                                ? faChevronDown
                                : faChevronRight
                            }
                            className="text-blue-300"
                          />
                          <span className="ml-4 duration-200 origin-left">
                            {submodule.Submodule_Name}
                          </span>
                        </button>

                        <AnimatePresence>
                          {expandedModules.has(submodule.Id) && (
                            <motion.div
                              initial="collapsed"
                              animate="open"
                              exit="collapsed"
                              variants={variants}
                            >
                              <ul>
                                {submodule.Pages.map((page) => (
                                  <li
                                    key={page.Page_Id}
                                    onClick={() => {
                                      navigate(page.Page_Url);

                                      onClose();
                                      // Log event when page is clicked
                                    }}
                                    className="w-full p-1 pl-8 text-base font-normal text-white transition duration-300 rounded-md cursor-pointer hover:bg-primary_dark hover:text-white "
                                  >
                                    <FontAwesomeIcon
                                      icon={getModuleIcon(page.Page_Icon)}
                                      className="text-xs text-blue-300"
                                    />
                                    <span
                                      className={`${
                                        !open && "md:hidden"
                                      } transition-all duration-200 ease-in-out group-hover:text-lg origin-left ml-4 inline-block`}
                                    >
                                      {page.Page_Name}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
      <div className="absolute inset-x-0 bottom-0 p-1">
        <div className="flex items-center mt-8 gap-x-4 lg:px-2">
          <button
            onClick={handleLogout}
            className="flex items-center justify-center w-full h-10 mx-auto mb-2 font-medium transition duration-300 border-none rounded place-items-end justify-self-end group bg-primary_dark text-white focus:outline-none hover:bg-secondary_shade"
          >
            <span className="transition-all duration-200 ease-in-out group-hover:text-lg">
              <LogoutOutlined />

              <span className={`${!open && "hidden"}`}>Logout</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Function to map module icon name to Font Awesome icon
const getModuleIcon = (iconName: string) => {
  switch (iconName) {
    case "User":
      return faUser;
    case "Document":
      return faFile;
    case "People":
      return faPeopleArrows;
    case "gears":
      return faCog;
    case "Heart":
      return faHeart;
    case "Chart":
      return faChartBar;
    case "Calendar":
      return faCalendar;
    case "ShoppingCart":
      return faShoppingCart;
    case "Cog":
      return faCog;
    case "Envelope":
      return faEnvelope;
    case "user-shield":
      return faUserShield;
    case "location-dot":
      return faLocationDot;
    case "file-lines":
      return faFileLines;
    case "circle-half-stroke":
      return faCircleHalfStroke;
    case "file-shield":
      return faFileShield;
    default:
      return faUser; // You can return a default icon if the module icon name doesn't match any of the cases
  }
};

export default Sidebar;
